import React, { useState, useEffect } from "react";
import { Button, DatePicker, Input, Select, Switch } from "antd";
import moment from "moment";
import { ErrorToast, SuccessToast } from "../../../../../widgets/AppToast";
import { post } from "../../../../../services/services";
import { ApiURL } from "../../../../../services/apiConstants";
import useUserData from "../../../../../hooks/useUserData";
import UnitWithInput from "../../../../../components/UnitWithInput";
import {
  currentUnits,
  pressureUnits,
  speedUnits,
  temperatureUnits,
  volumeUnits,
} from "../../../../../Data/data";
import dayjs from "dayjs";
import { useDate } from "../../../../../context/DateContext";

// Map parameter names to labels and unit options
const parameterConfig = {
  hydraulicoilpressuresupply: {
    label: "Hydraulic Oil Pressure - Supply",
    unitList: pressureUnits,
  },
  hydraulicoilpressurereturn: {
    label: "Hydraulic Oil Pressure - Return",
    unitList: pressureUnits,
  },
  hydraulicoiltemperature: {
    label: "Hydraulic Oil Temperature (°C)",
    unitList: temperatureUnits,
  },
  hydraulicoillevel: { label: "Hydraulic Oil Level", unitList: [] },
  pumpspeedrpm: { label: "Pump Speed (RPM)", unitList: speedUnits },
  dischargepressure: { label: "Discharge Pressure", unitList: pressureUnits },
  suctionpressurevacuum: {
    label: "Suction Pressure (Vacuum)",
    unitList: pressureUnits,
  },
  vibrationlevel: { label: "Vibration Level (mm/s RMS)", unitList: [] },
  electricmotorcurrentpowerpack: {
    label: "Electric Motor Current - Power Pack",
    unitList: currentUnits,
  },
  electricpowerconsumption: {
    label: "Electric Power Consumption",
    unitList: [],
  },
  runninghours: { label: "Running Hours", unitList: [] },
  flowrate: { label: "Flow Rate (m³/hr)", unitList: [] },
};

export const FramoLog = ({ eng_log_basic_id, _items }) => {
  const user = useUserData();
  const { selectedDate } = useDate();
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [staffDashboard, setStaffDashboard] = useState(null);
  const [formData, setFormData] = useState({
    logDate: null,
    parameters: {},
    otherParams: {},
  });
  const [checked, setChecked] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(false);

  useEffect(() => {
    const staffData = localStorage.getItem("staff_dashboard");
    const parsedData = staffData ? JSON.parse(staffData) : null;
    setStaffDashboard(parsedData);
    const initialParameters = (_items?.selected_parameters || []).reduce(
      (acc, param) => {
        if (parameterConfig[param]) {
          acc[param] = {
            value: "",
            unit: parameterConfig[param].unitList[0] || "",
          };
        }
        return acc;
      },
      {}
    );

    const initialOtherParams = (_items?.other_parameters || []).reduce(
      (acc, param) => {
        acc[param.name] = { value: param.value || "", unit: param.unit || "" };
        return acc;
      },
      {}
    );

    setFormData((prev) => ({
      ...prev,
      parameters: initialParameters,
      otherParams: initialOtherParams,
    }));

    if (parsedData && _items?.id) {
      VIEW_NITROGEN_LOGBOOK_API(_items.id, parsedData);
    }
  }, [_items?.id]);

  // Handle date/time change
  const onChangeDateTime = (value) => {
    setFormData((prev) => ({
      ...prev,
      logDate: value ? value.format("YYYY-MM-DD HH:mm:ss") : null,
    }));
  };

  // Handle parameter value/unit change
  const updateParameter = (param, field, value) => {
    setFormData((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [param]: { ...prev.parameters[param], [field]: value },
      },
    }));
  };

  // Handle other parameter value/unit change
  const updateOtherParam = (name, field, value) => {
    setFormData((prev) => ({
      ...prev,
      otherParams: {
        ...prev.otherParams,
        [name]: { ...prev.otherParams[name], [field]: value },
      },
    }));
  };

  // API to add new logbook entry
  const ADD_NITROGEN_LOGBOOK_API = (framo_id, framo_on_off) => {
    setLoader(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append(
        "date_time_log",
        formData.logDate || moment().format("YYYY-MM-DD HH:mm:ss")
      );
      FORM_DATA.append("eng_log_basic_id", Number(eng_log_basic_id) || 0);
      FORM_DATA.append(
        "voyage_id",
        Number(staffDashboard?.voyage_details?.[0]?.id) || 0
      );
      FORM_DATA.append("framo_id", Number(framo_id) || 0);
      FORM_DATA.append("framo_on_off", framo_on_off ? "true" : "false");
      const selectedParams = {};
      Object.entries(formData.parameters).forEach(([key, { value, unit }]) => {
        selectedParams[key] = { value: Number(value) || "", unit: unit || "" };
      });
      FORM_DATA.append("selected_parameters", JSON.stringify(selectedParams));
      const otherParams = Object.entries(formData.otherParams).reduce(
        (acc, [key, { value, unit }]) => {
          acc[key] = { value: value || "", unit: unit || "" };
          return acc;
        },
        {}
      );
      FORM_DATA.append("other_parameters", JSON.stringify(otherParams));

      post(ApiURL.add_cargo_machinery_framo_st, FORM_DATA).then((res) => {
        setLoader(false);
        if (res?.status === true) {
          SuccessToast(
            "Logbook Add Success!",
            "IGG logbook details added successfully!"
          );
          VIEW_NITROGEN_LOGBOOK_API(
            res?.framo_cargo_data?.framo_id,
            staffDashboard
          );
        } else {
          setErrors(res?.errors || {});
          ErrorToast("Error", "Failed to add logbook details.");
        }
      });
    } catch (error) {
      setLoader(false);
      console.error("Error adding IGG logbook:", error);
      ErrorToast("Error", "An error occurred while adding logbook details.");
    }
  };

  // API to update existing logbook entry
  const UPDATE_NITROGEN_LOGBOOK_API = (framo_id, save_submit, framo_on_off) => {
    setLoader(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append(
        "date_time_log",
        formData.logDate || moment().format("YYYY-MM-DD HH:mm:ss")
      );
      FORM_DATA.append("save_submit", Number(save_submit) || 0);
      FORM_DATA.append("framo_on_off", framo_on_off ? "true" : "false");
      FORM_DATA.append("eng_log_basic_id", Number(eng_log_basic_id) || 0);
      FORM_DATA.append(
        "voyage_id",
        Number(staffDashboard?.voyage_details?.[0]?.id) || 0
      );
      FORM_DATA.append("framo_id", Number(framo_id) || 0);
      const selectedParams = {};
      Object.entries(formData.parameters).forEach(([key, { value, unit }]) => {
        selectedParams[key] = { value: value, unit: unit || "" };
      });
      FORM_DATA.append("selected_parameters", JSON.stringify(selectedParams));
      const otherParams = Object.entries(formData.otherParams).reduce(
        (acc, [key, { value, unit }]) => {
          acc[key] = { value: value || "", unit: unit || "" };
          return acc;
        },
        {}
      );
      FORM_DATA.append("other_parameters", JSON.stringify(otherParams));

      post(ApiURL.view_update_cargo_machinery_framo_st, FORM_DATA).then(
        (res) => {
          setLoader(false);
          if (res?.status === true) {
            SuccessToast(
              "Logbook Update Success!",
              res?.message || "Logbook details updated successfully!"
            );
            VIEW_NITROGEN_LOGBOOK_API(
              res?.framo_cargo_data?.framo_id,
              staffDashboard
            );
          } else {
            setErrors(res?.errors || {});
            ErrorToast("Error", "Failed to update logbook details.");
          }
        }
      );
    } catch (error) {
      setLoader(false);
      console.error("Error updating IGG logbook:", error);
      ErrorToast("Error", "An error occurred while updating logbook details.");
    }
  };

  // API to view logbook details
  const VIEW_NITROGEN_LOGBOOK_API = async (framo_id, staff_dashboard) => {
    setLoader(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("framo_id", framo_id);
      FORM_DATA.append("eng_log_basic_id", eng_log_basic_id);
      FORM_DATA.append("voyage_id", staff_dashboard?.voyage_details?.[0]?.id);
      post(ApiURL.view_update_cargo_machinery_framo_st, FORM_DATA).then(
        (res) => {
          setLoader(false);

          if (res?.status === true) {
            const framoData = res?.framo_cargo_data;
            setUpdateStatus(framoData?.complete_status || false);
            setSubmitStatus(res?.submit_status || false);
            setChecked(framoData?.framo_on_off || false);

            const parameters = Object.keys(
              framoData?.selected_parameters || {}
            ).reduce((acc, param) => {
              const paramData = framoData.selected_parameters[param] || {
                value: 0,
                unit: "",
              };
              acc[param] = {
                value:
                  paramData.value !== undefined
                    ? paramData.value.toString()
                    : "0",
                unit:
                  paramData.unit || parameterConfig[param]?.unitList[0] || "",
              };
              return acc;
            }, {});

            setFormData((prev) => {
              return {
                ...prev,
                logDate:
                  framoData?.date_time_log ||
                  moment().format("YYYY-MM-DD HH:mm:ss"),
                parameters,

                otherParams:
                  framoData?.other_parameters &&
                  Object.keys(framoData.other_parameters).length
                    ? Object.keys(framoData.other_parameters).reduce(
                        (acc, key) => {
                          const paramData = framoData.other_parameters[key] || {
                            value: "",
                            unit: "",
                          };
                          acc[key] = {
                            value:
                              paramData.value !== undefined
                                ? paramData.value.toString()
                                : "",
                            unit: paramData.unit || "",
                          };
                          return acc;
                        },
                        {}
                      )
                    : prev.otherParams,
              };
            });
          } else {
            setErrors(res?.errors || {});
            ErrorToast("Error", "Failed to fetch logbook details.");
          }
        }
      );
    } catch (error) {
      setLoader(false);
      console.error("Error fetching IGG logbook:", error);
      ErrorToast("Error", "An error occurred while fetching logbook details.");
    }
  };

  // Render
  return (
    <>
      <div className="row custom-box">
        <div className="col-4">
          <b style={{ color: "#161515", fontSize: "14px" }}>
            Framo Name: {_items?.framo_name}
          </b>
        </div>
        <div className="col-4">
          <b style={{ color: "#161515", fontSize: "14px" }}>
            {" "}
            Framo Maker: {_items?.framo_maker}
          </b>
        </div>
        <div className="col-4">
          <b style={{ color: "#161515", fontSize: "14px" }}>
            {" "}
            Framo Model: {_items?.framo__model}
          </b>
        </div>
        <div className="col-4">
          <b style={{ color: "#161515", fontSize: "14px" }}>
            {" "}
            Framo id: {_items?.id}
          </b>
        </div>
        <div className="col-2">
          <Switch
            disabled={submitStatus}
            checked={checked}
            onChange={(value) => {
              setChecked(value);
              updateStatus
                ? UPDATE_NITROGEN_LOGBOOK_API(_items?.id, "0", value)
                : ADD_NITROGEN_LOGBOOK_API(_items?.id, value);
            }}
            checkedChildren="On"
            unCheckedChildren="Off"
            style={{ backgroundColor: checked ? "green" : "red" }}
          />
        </div>
      </div>

      {checked && (
        <>
          {/* Date & Time */}
          <div className="row">
            <div className="col-4">
              <div className="input-box">
                <label>Date & Time</label>
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Select Date & Time"
                  // value={formData.logDate ? dayjs(formData.logDate, "YYYY-MM-DD HH:mm:ss") : null}
                  value={
                    formData.logDate
                      ? dayjs(formData.logDate, "YYYY-MM-DD HH:mm:ss")
                      : selectedDate
                      ? dayjs(selectedDate, "YYYY-MM-DD HH:mm:ss")
                      : null
                  }
                  onChange={onChangeDateTime}
                  style={{ width: "100%" }}
                  // disabled={submitStatus}
                  disabled
                  className="custom-input"
                />
                {errors.date_time_log && (
                  <p className="error-text">{errors.date_time_log}</p>
                )}
              </div>
            </div>
          </div>

          {/* Selected Parameters */}
          {(() => {
            const rows = [];
            const inputs = (_items?.selected_parameters || [])
              .filter((param) => parameterConfig[param])
              .map((param) => ({
                type: "selectedParam",
                name: param,
                config: parameterConfig[param],
                value: formData.parameters[param]?.value || "",
                unit: formData.parameters[param]?.unit || "",
              }));

            for (let i = 0; i < inputs.length; i += 3) {
              const group = inputs.slice(i, i + 3);
              rows.push(
                <div className="row" key={`param-row-${i}`}>
                  {group.map((input) => (
                    <div className="col-4" key={input.name}>
                      {input.config.dropdownOptions ? (
                        <div className="input-box">
                          <label>{input.config.label}</label>
                          <Select
                            disabled={submitStatus}
                            value={input.value}
                            onChange={(value) =>
                              updateParameter(input.name, "value", value)
                            }
                            className="custom-input"
                            style={{ width: "100%" }}
                            placeholder={`Select ${input.config.label}`}
                          >
                            {input.config.dropdownOptions.map((option) => (
                              <Select.Option key={option} value={option}>
                                {option}
                              </Select.Option>
                            ))}
                          </Select>
                          {errors[
                            `pr_${input.name
                              .replace(/([A-Z])/g, "_$1")
                              .toLowerCase()}`
                          ] && (
                            <p className="error-text">
                              {
                                errors[
                                  `pr_${input.name
                                    .replace(/([A-Z])/g, "_$1")
                                    .toLowerCase()}`
                                ]
                              }
                            </p>
                          )}
                        </div>
                      ) : input.config.unitList?.length ? (
                        <UnitWithInput
                          label={input.config.label}
                          value={input.value}
                          onChange={(e) =>
                            updateParameter(input.name, "value", e.target.value)
                          }
                          unit={input.unit}
                          onUnitChange={(e) =>
                            updateParameter(input.name, "unit", e.target.value)
                          }
                          disabled={submitStatus}
                          error={
                            errors[
                              `pr_${input.name
                                .replace(/([A-Z])/g, "_$1")
                                .toLowerCase()}`
                            ]
                          }
                          unitList={input.config.unitList}
                          className="custom-input"
                        />
                      ) : (
                        <div className="input-box">
                          <label>{input.config.label}</label>
                          <Input
                            disabled={submitStatus}
                            value={input.value}
                            placeholder={input.config.label}
                            onChange={(e) =>
                              updateParameter(
                                input.name,
                                "value",
                                e.target.value
                              )
                            }
                            className={`custom-input ${
                              errors[
                                `pr_${input.name
                                  .replace(/([A-Z])/g, "_$1")
                                  .toLowerCase()}`
                              ]
                                ? "error"
                                : ""
                            }`}
                          />
                          {errors[
                            `pr_${input.name
                              .replace(/([A-Z])/g, "_$1")
                              .toLowerCase()}`
                          ] && (
                            <p className="error-text">
                              {
                                errors[
                                  `pr_${input.name
                                    .replace(/([A-Z])/g, "_$1")
                                    .toLowerCase()}`
                                ]
                              }
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {group.length < 3 &&
                    Array.from({ length: 3 - group.length }).map((_, idx) => (
                      <div
                        className="col-4"
                        key={`empty-param-${i}-${idx}`}
                      ></div>
                    ))}
                </div>
              );
            }
            return rows;
          })()}

          {/* Other Parameters */}
          {(() => {
            const rows = [];
            const otherParamsArray = Object.entries(formData.otherParams || {});
            for (let i = 0; i < otherParamsArray.length; i += 3) {
              const group = otherParamsArray.slice(i, i + 3);
              rows.push(
                <div className="row" key={`other-row-${i}`}>
                  {group.map(([name, { value, unit }]) => (
                    <div className="col-4" key={name}>
                      <UnitWithInput
                        label={name}
                        value={value}
                        onChange={(e) =>
                          updateOtherParam(name, "value", e.target.value)
                        }
                        unit={unit}
                        onUnitChange={(e) =>
                          updateOtherParam(name, "unit", e.target.value)
                        }
                        disabled={submitStatus}
                        unitList={[unit]}
                        className="custom-input"
                      />
                    </div>
                  ))}
                  {group.length < 3 &&
                    Array.from({ length: 3 - group.length }).map((_, idx) => (
                      <div
                        className="col-4"
                        key={`empty-other-${i}-${idx}`}
                      ></div>
                    ))}
                </div>
              );
            }
            return rows;
          })()}

          {/* Buttons */}
          <div
            className="row"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              marginLeft: "5px",
            }}
          >
            {["Chief Engineer"].includes(user?.data?.designation) &&
              eng_log_basic_id && (
                <Button
                  disabled={submitStatus}
                  loading={loader}
                  className="save_btn"
                  style={{ color: "white" }}
                  onClick={() =>
                    updateStatus
                      ? UPDATE_NITROGEN_LOGBOOK_API(_items?.id, "1", checked)
                      : ADD_NITROGEN_LOGBOOK_API(_items?.id, checked)
                  }
                >
                  Submit
                </Button>
              )}
            <Button
              disabled={submitStatus}
              loading={loader}
              className="save_btn"
              style={{ color: "white" }}
              onClick={() =>
                updateStatus
                  ? UPDATE_NITROGEN_LOGBOOK_API(_items?.id, "0", checked)
                  : ADD_NITROGEN_LOGBOOK_API(_items?.id, checked)
              }
            >
              Save
            </Button>
          </div>
        </>
      )}
    </>
  );
};
